import { useState, Fragment, useEffect } from 'react'
import { Box, Button, IconButton, Menu as MuiMenu, MenuItem as MuiMenuItem, Typography, Badge } from '@mui/material'
import { styled } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import PerfectScrollbarComponent from 'react-perfect-scrollbar'
import CustomChip from 'src/@core/components/mui/chip'
import CustomAvatar from 'src/@core/components/mui/avatar'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import { axiosInstance } from 'src/network/adapter'
import { ApiEndPoints } from 'src/network/endpoints'
import { toastError } from 'src/utils/utils'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

const Menu = styled(MuiMenu)(({ theme }) => ({
  '& .MuiMenu-paper': {
    width: 380,
    overflow: 'hidden',
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  '& .MuiMenu-list': {
    padding: 0
  }
}))

const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`
}))

const styles = {
  maxHeight: 349,
  '& .MuiMenuItem-root:last-of-type': {
    border: 0
  }
}

const PerfectScrollbar = styled(PerfectScrollbarComponent)({
  ...styles
})

const Avatar = styled(CustomAvatar)({
  width: '2.375rem',
  height: '2.375rem',
  fontSize: '1.125rem'
})

const MenuItemTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  //flex: '1 1 100%',
  overflow: 'hidden',
  fontSize: '0.875rem',
  whiteSpace: 'nowrap',
  //textOverflow: 'ellipsis',
  marginBottom: theme.spacing(0.75)
}))

const MenuItemSubtitle = styled(Typography)({
  // flex: '1 1 100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textWrap: 'wrap',
  maxWidth: 220
  // textOverflow: 'ellipsis'
})

const NotificationDropdown = props => {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'
  const [loading, setLoading] = useState(false)
  const [notificationData, setNotificationData] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const fetchData = () => {
    setLoading(true)
    axiosInstance
      .get(ApiEndPoints.NOTIFICATION.list, {
        params: {
          isRead: 'unread'
        }
      })
      .then(response => {
        setNotificationData(response.data.data?.notifications)
        setUnreadCount(response.data.data?.unreadCount)
      })
      .catch(error => {
        toastError(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    fetchData()
    const handleUpdate = () => fetchData()
    window.addEventListener('notificationsUpdated', handleUpdate)
    return () => {
      window.removeEventListener('notificationsUpdated', handleUpdate)
    }
  }, [])

  const onSubmit = id => {
    setLoading(true)
    axiosInstance
      .patch(ApiEndPoints.NOTIFICATION.markAllAsRead, { notification_id: id }, { params: { notification_id: id } })
      .then(response => {
        window.dispatchEvent(new Event('notificationsUpdated'))
      })
      .catch(error => {
        toastError(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const { settings } = props
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null)
  const hidden = useMediaQuery(theme => theme.breakpoints.down('lg'))
  const { direction } = settings

  const handleDropdownOpen = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleDropdownClose = () => {
    setAnchorEl(null)
  }

  const markAllAsRead = () => {
    setLoading(true)
    axiosInstance
      .patch(ApiEndPoints.NOTIFICATION.markAllAsRead)
      .then(response => {
        fetchData()
      })
      .catch(error => {
        toastError(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const ScrollWrapper = ({ children }) => {
    if (hidden) {
      return <Box sx={{ ...styles, overflowY: 'auto', overflowX: 'hidden' }}>{children}</Box>
    } else {
      return (
        <PerfectScrollbar options={{ wheelPropagation: false, suppressScrollX: true }}>{children}</PerfectScrollbar>
      )
    }
  }

  return (
    <Fragment>
      <IconButton color='inherit' aria-haspopup='true' onClick={handleDropdownOpen} aria-controls='customized-menu'>
        <Badge badgeContent={unreadCount} color='secondary'>
          <NotificationsActiveIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleDropdownClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'ltr' ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: direction === 'ltr' ? 'right' : 'left' }}
      >
        <MenuItem disableRipple>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography sx={{ fontWeight: 600 }}>{t('Notifications')}</Typography>
            <CustomChip
              skin='light'
              size='small'
              label={`${unreadCount} ${t('New')}`}
              color='primary'
              sx={{ height: 20, fontSize: '0.75rem', fontWeight: 500, borderRadius: '10px' }}
            />
          </Box>
        </MenuItem>

        <ScrollWrapper>
          {notificationData.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant='body2' color='secondary'>
                {t('No notifications yet')}
              </Typography>
            </Box>
          ) : (
            notificationData.map((notification, index) => (
              <MenuItem
                key={index}
                onClick={() => {
                  onSubmit(notification._id)
                  handleDropdownClose()
                }}
                sx={{
                  alignItems: 'flex-start',
                  position: 'relative',
                  transition: 'all 0.25s ease',

                  ...(!notification.is_admin_read && {
                    background: theme => `${theme.palette.primary.main}08`
                  }),

                  '&:hover': {
                    transform: 'translateY(-1px)',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'flex-start'
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <Avatar alt={notification.title} src='/images/avatars/4.png' />

                    {!notification.is_admin_read && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: 9,
                          height: 9,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          border: '2px solid white',
                          boxShadow: theme => `0 0 0 1px ${theme.palette.primary.main}30`,
                          animation: 'pulse 1.5s infinite',

                          '@keyframes pulse': {
                            '0%': {
                              transform: 'scale(1)',
                              boxShadow: theme => `0 0 0 0px ${theme.palette.primary.main}30`
                            },
                            '70%': {
                              transform: 'scale(1.2)',
                              boxShadow: theme => `0 0 0 4px ${theme.palette.primary.main}00`
                            },
                            '100%': {
                              transform: 'scale(1)',
                              boxShadow: theme => `0 0 0 0px ${theme.palette.primary.main}00`
                            }
                          }
                        }}
                      />
                    )}
                  </Box>

                  <Box
                    sx={{
                      mx: 4,
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden'
                    }}
                  >
                    <MenuItemTitle
                      sx={{
                        fontWeight: !notification.is_admin_read ? 600 : 500,
                        color: !notification.is_admin_read ? 'text.primary' : 'text.secondary'
                      }}
                    >
                      {isArabic ? (notification.title_ar || notification.title) : notification.title}
                    </MenuItemTitle>

                    <MenuItemSubtitle
                      variant='body2'
                      sx={{
                        color: !notification.is_admin_read ? 'text.primary' : 'text.secondary',
                        opacity: !notification.is_admin_read ? 0.85 : 0.65
                      }}
                    >
                      {isArabic ? (notification.body_ar || notification.body) : notification.body}
                    </MenuItemSubtitle>
                  </Box>

                  <Typography
                    variant='caption'
                    sx={{
                      color: !notification.is_admin_read ? 'primary.main' : 'text.disabled',
                      fontWeight: !notification.is_admin_read ? 700 : 400,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {moment(notification.createdAt).fromNow()}
                  </Typography>
                </Box>
              </MenuItem>
            ))
          )}
        </ScrollWrapper>
        {notificationData.length > 0 && (
          <MenuItem
            disableRipple
            sx={{ py: 3.5, borderBottom: 0, borderTop: theme => `1px solid ${theme.palette.divider}` }}
          >
            <Button
              variant='contained'
              onClick={() => {
                handleDropdownClose()
                setTimeout(() => {
                  navigate('/notifications')
                }, 300)
              }}
              sx={{ textDecoration: 'none', width: '100%', textAlign: 'center' }}
            >
              {t('View All Notifications')}
            </Button>
          </MenuItem>
        )}
      </Menu>
    </Fragment>
  )
}

export default NotificationDropdown
